import React, { useState, useEffect } from 'react';
import { getPortForwardingConfigs, PortForwardingConfig, getGatewayDeviceId, checkIsDeviceConnectedByDeviceId } from '../../Services/serviceURL';
import { toast } from 'react-toastify';
import { usePagination } from '../../hooks/usePagination';
import searchIcon from '../../Images/search-icon.svg';
import infoIcon from '../../Images/info-circle_orange.png';
import './RemoteAccess.css';
import {
  clonePortForwardingConfigs,
  emptyPortFieldErrors,
  formatAccessLimit,
  formatTLS,
  isConfigExpired,
  validatePortForwardingForm,
} from './remoteAccessUtils';
import { handlePortForwardingSaveError, savePortForwardingConfiguration } from './remoteAccessSave';

interface RemoteAccessProps {
  selectedAssetRecordId: string | null;
}

const RemoteAccess: React.FC<RemoteAccessProps> = ({ selectedAssetRecordId }) => {
  // State for port forwarding configurations
  const [portForwardingConfigs, setPortForwardingConfigs] = useState<PortForwardingConfig[]>([]);
  const [loadingPortConfigs, setLoadingPortConfigs] = useState<boolean>(false);
  const [remoteSearchTerm, setRemoteSearchTerm] = useState<string>('');
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  // State for port forwarding modal
  const [showAddPortModal, setShowAddPortModal] = useState<boolean>(false);
  const [showAccessModal, setShowAccessModal] = useState<boolean>(false);
  const [selectedConfig, setSelectedConfig] = useState<PortForwardingConfig | null>(null);
  const [isDeviceConnected, setIsDeviceConnected] = useState<boolean>(false);
  const [checkingConnection, setCheckingConnection] = useState<boolean>(false);
  
  // State for collapsible sections in details modal
  const [isBasicDetailsExpanded, setIsBasicDetailsExpanded] = useState<boolean>(false);
  const [isAccessDetailsExpanded, setIsAccessDetailsExpanded] = useState<boolean>(true);
  
  const [portFormData, setPortFormData] = useState({
    serviceName: '',
    inputPort: '6001', // Default to 6001 
    targetPort: '80',
    targetIpAddress: '192.168.0.5',
    protocol: 'TCP',
    accessLimit: '60', // Default to 60 minutes (first option)
    tls: true // Default TLS to checked
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState({
    serviceName: '',
    inputPort: '',
    targetPort: '',
    targetIpAddress: ''
  });

  // API function to fetch port forwarding configurations
  const fetchPortForwardingConfigs = async (assetId: string) => {
    if (!assetId) return;
    
    setLoadingPortConfigs(true);
    try {
      const configs = await getPortForwardingConfigs(assetId);
      setPortForwardingConfigs(configs);
    } catch (error: any) {
      console.error('Error fetching port forwarding configs:', error);
      setPortForwardingConfigs([]);
    } finally {
      setLoadingPortConfigs(false);
    }
  };

  // Effect to fetch data when selectedAssetRecordId changes
  useEffect(() => {
    if (selectedAssetRecordId) {
      fetchPortForwardingConfigs(selectedAssetRecordId);
    }
  }, [selectedAssetRecordId]);

  useEffect(() => {
    if (portForwardingConfigs.length === 0) return;

    const refreshExpiryDisplay = () => {
      setPortForwardingConfigs((prev) => clonePortForwardingConfigs(prev));
    };

    const intervalId = setInterval(refreshExpiryDisplay, 10000);
    return () => clearInterval(intervalId);
  }, [portForwardingConfigs.length]);

  // Modal handlers for port forwarding
  const handleAddPortForwardingClick = () => {
    setShowAddPortModal(true);
  };

  const handleClosePortModal = () => {
    setShowAddPortModal(false);
    setIsSaving(false);
    setFieldErrors({
      serviceName: '',
      inputPort: '',
      targetPort: '',
      targetIpAddress: ''
    });
    setPortFormData({
      serviceName: '',
      inputPort: '',
      targetPort: '80',
      targetIpAddress: '192.168.0.5',
      protocol: 'TCP',
      accessLimit: '15', // Default to 15 minutes (first option)
      tls: true // Default TLS to checked
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setPortFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const refreshConfigs = async () => {
    if (selectedAssetRecordId) {
      await fetchPortForwardingConfigs(selectedAssetRecordId);
    }
  };

  const handleSavePortForwarding = async () => {
    if (!selectedAssetRecordId) {
      toast.error('No device ID available for saving port forwarding config');
      return;
    }

    const validation = validatePortForwardingForm(portFormData);
    if (!validation.ok) {
      if (validation.fieldErrors) setFieldErrors(validation.fieldErrors);
      if (validation.toastMessage) toast.error(validation.toastMessage);
      return;
    }

    setFieldErrors(emptyPortFieldErrors());
    setIsSaving(true);

    try {
      await savePortForwardingConfiguration({
        assetRecordId: selectedAssetRecordId,
        form: portFormData,
        onCloseModal: handleClosePortModal,
        refreshConfigs,
      });
    } catch (error) {
      handlePortForwardingSaveError(error, refreshConfigs);
    } finally {
      setIsSaving(false);
    }
  };

  // Filter port forwarding configs based on search term and exclude incomplete configs
  const filteredPortConfigs = portForwardingConfigs.filter(config => {
    // First filter out configs with empty service names (incomplete configs)
    if (!config.serviceName || config.serviceName.trim() === '') {
      return false;
    }
    
    // Then apply search filter
    if (!remoteSearchTerm) return true;
    const searchLower = remoteSearchTerm.toLowerCase();
    return (
      config.serviceName?.toLowerCase().includes(searchLower) ||
      config.userName?.toLowerCase().includes(searchLower) ||
      config.targetIpAddress?.toLowerCase().includes(searchLower) ||
      config.protocol?.toLowerCase().includes(searchLower) ||
      config.inputPort?.toString().includes(searchLower) ||
      config.targetPort?.toString().includes(searchLower)
    );
  });

  // Use pagination hook
  const {
    currentPage,
    totalPages,
    currentData: paginatedConfigs,
    totalItems,
    goToPage,
    goToPreviousPage,
    goToNextPage,
  } = usePagination({
    data: filteredPortConfigs,
    itemsPerPage: itemsPerPage,
  });

  const getActionButtonClass = (config: PortForwardingConfig): string => {
    const expired = isConfigExpired(config);
    return `action-btn ${expired ? 'expired' : 'active'}`;
  };

  const getActionButtonText = (config: PortForwardingConfig): string => {
    const expired = isConfigExpired(config);
    return expired ? 'Expired' : 'Active';
  };

  const handleActiveNavigation = (config: PortForwardingConfig) => {
    // Build HTTP/HTTPS URL from config data
    const hostname = config.hostname || config.endpoint;
    const port = config.port; // Use config.port instead of config.inputPort for the external URL
    
    if (hostname && port) {
      // Use HTTPS if TLS is enabled, otherwise HTTP
      const protocol = config.tls ? 'https' : 'http';
      const url = `${protocol}://${hostname}:${port}`;
      
      
      // Open in new tab/window
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('Unable to navigate: Hostname or port not available');
    }
  };

  const handleInfoIconClick = (configId: string) => {
    
    // Handle empty or invalid config IDs
    if (!configId || configId.trim() === '') {
      console.error('Invalid config ID:', configId);
      toast.error('Invalid configuration ID');
      return;
    }
    
    // Find the config from the current local state
    const config = portForwardingConfigs.find(c => c.id === configId);
    
    if (!config) {
      console.error('Config not found for ID:', configId);
      toast.error('Configuration not found');
      return;
    }

    // Directly set the config and open modal without connection checking
    setSelectedConfig(config);
    setIsDeviceConnected(true); // Assume connected for info display
    setShowAccessModal(true);
  };

  const handleAccessButtonClick = async (configId: string) => {
    
    // Handle empty or invalid config IDs
    if (!configId || configId.trim() === '') {
      console.error('Invalid config ID:', configId);
      toast.error('Invalid configuration ID');
      return;
    }
    
    try {
      // Since GetPortforwardingConfigById endpoint doesn't exist (404),
      // we'll find the config from the current local state instead
      const config = portForwardingConfigs.find(c => c.id === configId);
      
      if (!config) {
        console.error('Config not found for ID:', configId);
        toast.error('Configuration not found');
        return;
      }

      setSelectedConfig(config);
      setCheckingConnection(true);
      
      // First get the gateway device ID for this asset
      if (selectedAssetRecordId) {
        const gatewayDeviceId = await getGatewayDeviceId(selectedAssetRecordId);
        
        if (gatewayDeviceId) {
          // Check if device is connected
          const connectionStatus = await checkIsDeviceConnectedByDeviceId(gatewayDeviceId);
          setIsDeviceConnected(connectionStatus);
        } else {
          console.warn('Gateway Device ID not found');
          setIsDeviceConnected(false);
        }
      }
    } catch (error) {
      console.error('Error checking device connection:', error);
      setIsDeviceConnected(false);
      toast.error('Failed to load configuration details');
    } finally {
      setCheckingConnection(false);
      setShowAccessModal(true);
    }
  };

  const handleCloseAccessModal = () => {
    setShowAccessModal(false);
    setSelectedConfig(null);
    setIsDeviceConnected(false);
    setCheckingConnection(false);
  };

  return (
    <div className="tab-content remote-tab-content">
      <div className="remote-control-container">
        {/* Main Content */}
        <div className="remote-main-content">
          {/* Controls */}
          <div className="remote-controls">
            <div className="remote-search-container">
              <input 
                type="text" 
                placeholder="Filter By Keyword" 
                className="remote-search-input"
                value={remoteSearchTerm}
                onChange={(e) => setRemoteSearchTerm(e.target.value)}
              />
              <img src={searchIcon} alt="Search" className="remote-search-icon" />
            </div>
            <div className="remote-actions">
              <button className="remote-add-btn" onClick={handleAddPortForwardingClick}>
                + Request Remote Access
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="remote-table-container slim-scroll">
            <table className="remote-table">
              <thead>
                <tr>
                  <th>User </th>
                  <th>Input Port </th>
                  <th>Target Port</th>
                  <th>Target IP Address </th>
                  <th>Protocol</th>
                  <th>Access Limit</th>
                  <th>TLS </th>
                  <th>Created Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loadingPortConfigs ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
                      Loading Port Forwarding Configurations...
                    </td>
                  </tr>
                ) : filteredPortConfigs.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: '20px', color: '#a0a0a0' }}>
                      {portForwardingConfigs.length === 0 
                        ? 'No Port Forwarding Configurations Found'
                        : 'No Configurations Match Your Search Criteria'
                      }
                    </td>
                  </tr>
                ) : (
                  paginatedConfigs.map((config, index) => (
                    <tr key={config.id || `config-${index}`}>
                      <td>{config.userName || config.createdBy || 'Unknown'}</td>
                      <td>{config.inputPort || config.port || '-'}</td>
                      <td>{config.targetPort || '-'}</td>
                      <td>{config.targetIpAddress || '-'}</td>
                      <td>{config.protocol?.toUpperCase() || 'TCP'}</td>
                      <td>{formatAccessLimit(config.accessLimit || 0)}</td>
                      <td>{formatTLS(config.tls || false)}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '33px' }}>
                          {(() => {
                            const expired = isConfigExpired(config);
                            return (
                              <>
                                <span 
                                  className={`status-text ${expired ? 'disabled' : 'clickable'}`}
                                  style={{ 
                                    cursor: expired ? 'default' : 'pointer',
                                    color: expired ? '#999' : '#007bff'
                                  }}
                                  onClick={() => !expired && handleActiveNavigation(config)}
                                  title={!expired ? 'Click to navigate to Soroccom login' : ''}
                                >
                                  {checkingConnection && selectedConfig?.id === config.id 
                                    ? 'Checking...' 
                                    : getActionButtonText(config)
                                  }
                                </span>
                                {!expired && (
                                  <img 
                                    src={infoIcon} 
                                    alt="Info" 
                                    style={{ width: '16px', height: '16px', cursor: 'pointer', marginBottom:"-2px" }}
                                    onClick={() => config.id && handleInfoIconClick(config.id)}
                                    title="View remote access details"
                                  />
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="remote-pagination">
            <button 
              className="pagination-btn" 
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
            >
               <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
               <path d="M15.8333 10L4.16668 10M4.16668 10L10 15.8334M4.16668 10L10 4.16669" stroke="#FCFCFD" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/>
               </svg> Previous
            </button>
            
            <div className="pagination-center">
              <span className="pagination-info">
                {totalItems > 0 ? (
                  <>
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
                    {remoteSearchTerm && ` (filtered from ${portForwardingConfigs.length} total)`}
                  </>
                ) : (
                  'No entries to show'
                )}
              </span> 
              <span className="pagination-page">Page {currentPage} of {totalPages }</span>

            </div>

            <button 
              className="pagination-btn" 
              onClick={goToNextPage}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Next <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
             <path d="M4.16675 9.99996H15.8334M15.8334 9.99996L10.0001 4.16663M15.8334 9.99996L10.0001 15.8333" stroke="#FCFCFD" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/>
             </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Add Port Forwarding Modal */}
      {showAddPortModal && (
        <div className="modal-overlay">
          <div className="port-forward-modal">
            <div className="modal-header">
              <h3>Request Remote Access</h3>
              <button className="modal-close" onClick={handleClosePortModal}>×</button>
            </div>
            <div className="modal-content slim-scroll">

                <div className="form-group">
                  <label>Input port <span style={{ color: '#ff4757' }}>*</span><span style={{ 
                                             color: '#999', 
                                             fontSize: '12px', 
                                             marginLeft: '4px',
                                             fontWeight: 'normal'
                                          }}>
                                          (Range:6000-8000)
                                        </span></label>
                  <input
                    type="text"
                    name="inputPort"
                    value={portFormData.inputPort}
                    onChange={handleInputChange}
                    placeholder="e.g. 6000-8000"
                  />
                  {fieldErrors.inputPort && (
                    <small style={{ color: '#ff4757', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {fieldErrors.inputPort}
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Target port <span style={{ color: '#ff4757' }}>*</span></label>
                  <input
                    type="text"
                    name="targetPort"
                    value={portFormData.targetPort}
                    onChange={handleInputChange}
                    placeholder="e.g. 8080"
                  />
                  {fieldErrors.targetPort && (
                    <small style={{ color: '#ff4757', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      {fieldErrors.targetPort}
                    </small>
                  )}
                </div>

              <div className="form-group">
                <label>Target IP address <span style={{ color: '#ff4757' }}>*</span></label>
                <input
                  type="text"
                  name="targetIpAddress"
                  value={portFormData.targetIpAddress}
                  onChange={handleInputChange}
                  placeholder="e.g. 192.168.1.100"
                />
                {fieldErrors.targetIpAddress && (
                  <small style={{ color: '#ff4757', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    {fieldErrors.targetIpAddress}
                  </small>
                )}
              </div>

                <div className="form-group">
                  <label>Protocol</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="protocol"
                        value="TCP"
                        checked={portFormData.protocol === 'TCP'}
                        onChange={handleInputChange}
                      />
                      TCP
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name="protocol"
                        value="UDP"
                        checked={portFormData.protocol === 'UDP'}
                        onChange={handleInputChange}
                      />
                      UDP
                    </label>
                  </div>
                <div className="form-group">
                  <label>Access limit </label>
                  <select
                    name="accessLimit"
                    value={portFormData.accessLimit}
                    onChange={handleInputChange}
                  >
                    <option value="15">15 minutes</option>
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">1 Hour</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="tls"
                      checked={portFormData.tls}
                      onChange={handleInputChange}
                    />
                    TLS
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={handleClosePortModal}>
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-save" 
                onClick={handleSavePortForwarding}
                disabled={isSaving}
              >
                {isSaving ? 'Requesting...' : 'Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* On Demand Access Modal */}
      {showAccessModal && selectedConfig && (
        <div className="modal-overlay" onClick={handleCloseAccessModal}>
          <div className="access-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h1></h1>
              <button className="modal-close" onClick={handleCloseAccessModal}>×</button>
            </div>
            <div className="modal-content">
              <div className="access-details">

{/* Common Access Details Section */}
                <div className="collapsible-section">
                  <div 
                    className="section-header-remote" 
                    onClick={() => setIsAccessDetailsExpanded(!isAccessDetailsExpanded)}
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0',borderBottom: '1px solid #4a5568', marginBottom: '16px' }}
                  >
                    <span className="section-title" style={{ fontSize: '16px', fontWeight: '600', color: 'white' }}>Common Access Details</span>
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none"
                      style={{ 
                        transform: isAccessDetailsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}
                    >
                      <path d="M7 10l5 5 5-5z" fill="currentColor"/>
                    </svg>
                  </div>
                  
                  {isAccessDetailsExpanded && (
                    <div className="section-content">
                      <div className="detail-row">
                        <span className="detail-label">SSH</span>
                        <span className="detail-value">
                          {(selectedConfig.hostname || selectedConfig.endpoint)
                            ? `ssh -p ${selectedConfig.inputPort || 'PORT'} ${selectedConfig.hostname || selectedConfig.endpoint} -l user`
                            : 'Hostname not available'
                          }
                        </span>
                        <button 
                          className="copy-btn" 
                          onClick={() => (selectedConfig.hostname || selectedConfig.endpoint) && navigator.clipboard.writeText(`ssh -p ${selectedConfig.inputPort || 'PORT'} ${selectedConfig.hostname || selectedConfig.endpoint} -l user`)}
                          disabled={!(selectedConfig.hostname || selectedConfig.endpoint)}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>



                      <div className="detail-row">
                        <span className="detail-label">HTTPS</span>
                        <span className="detail-value">
                          {(selectedConfig.hostname || selectedConfig.endpoint) && selectedConfig.port
                            ? (
                                <a 
                                  href={`https://${selectedConfig.hostname || selectedConfig.endpoint}:${selectedConfig.port}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#007bff', textDecoration: 'underline' }}
                                >
                                  {`https://${selectedConfig.hostname || selectedConfig.endpoint}:${selectedConfig.port}`}
                                </a>
                              )
                            : 'Hostname or port not available'
                          }
                        </span>
                        <button 
                          className="copy-btn" 
                          onClick={() => (selectedConfig.hostname || selectedConfig.endpoint) && selectedConfig.port && navigator.clipboard.writeText(`https://${selectedConfig.hostname || selectedConfig.endpoint}:${selectedConfig.port}`)}
                          disabled={!(selectedConfig.hostname || selectedConfig.endpoint) || !selectedConfig.port}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Basic Details Section */}
                <div className="collapsible-section">
                  <div 
                    className="section-header-remote" 
                    onClick={() => setIsBasicDetailsExpanded(!isBasicDetailsExpanded)}
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #4a5568', marginBottom: '16px' }}
                  >
                    <span className="section-title" style={{ fontSize: '16px', fontWeight: '600', color: 'white' }}>Additional Details</span>
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none"
                      style={{ 
                        transform: isBasicDetailsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease'
                      }}
                    >
                      <path d="M7 10l5 5 5-5z" fill="currentColor"/>
                    </svg>
                  </div>
                  
                  {isBasicDetailsExpanded && (
                    <div className="section-content">
                      <div className="detail-row">
                        <span className="detail-label">SIM ID</span>
                        <span className="detail-value">{selectedConfig.simId || '-'}</span>
                        <button className="copy-btn" onClick={() => navigator.clipboard.writeText(selectedConfig.simId || '')}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">Service</span>
                        <span className="detail-value">{selectedConfig.destinationStrategy || 'IMSI_PRIORITIZED'}</span>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">Port</span>
                        <span className="detail-value">{selectedConfig.inputPort}</span>
                        <button className="copy-btn" onClick={() => navigator.clipboard.writeText(selectedConfig.inputPort?.toString() || '')}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">TLS</span>
                        <span className="detail-value">{selectedConfig.tls ? 'Yes' : 'No'}</span>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">Hostname</span>
                        <span className="detail-value">{selectedConfig.hostname || selectedConfig.endpoint || '-'}</span>
                        <button className="copy-btn" onClick={() => navigator.clipboard.writeText(selectedConfig.hostname || selectedConfig.endpoint || '')}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">IP address</span>
                        <span className="detail-value">{selectedConfig.targetIpAddress || '-'}</span>
                        <button className="copy-btn" onClick={() => navigator.clipboard.writeText(selectedConfig.targetIpAddress || '')}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>

                      <div className="detail-row">
                        <span className="detail-label">Access from</span>
                        <span className="detail-value">
                          {(() => {
                            const accessFrom = selectedConfig.sourceIPAddress || selectedConfig.accessFrom;
                            if (!accessFrom || accessFrom === '0.0.0.0') return 'Anywhere';
                            return accessFrom;
                          })()}
                        </span>
                        <button className="copy-btn" onClick={() => navigator.clipboard.writeText(selectedConfig.sourceIPAddress || selectedConfig.accessFrom || '')}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="currentColor"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemoteAccess;